import { useEffect, useState } from 'react'
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Card,
  Badge,
  ThemeIcon,
  List,
  Box,
  Divider,
  Paper,
  Progress,
  Flex,
  Anchor,
  Center,
  ActionIcon,
  Loader,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Brain,
  BookOpen,
  GraduationCap,
  BarChart3,
  Zap,
  Target,
  BookA,
  Check,
  ArrowRight,
  Trophy,
  Sun,
  Moon,
} from 'lucide-react'
import { paymentApi, type SatPlan } from '../../api/payment'
import { PublicLandingHeader } from '../../components/layout/PublicLandingHeader'

const COLORS = {
  background: 'var(--sat-bg)',
  terraCotta: 'var(--sat-blush)',
  grapefruit: 'var(--sat-red)',
  lemon: 'var(--sat-yellow)',
  eveningSea: 'var(--sat-green)',
  sunkenShip: 'var(--sat-text)',
  white: 'var(--mantine-color-white)',
}

function useLandingColors() {
  const theme = useMantineTheme()
  const computed = useComputedColorScheme('dark')
  const isDark = computed === 'dark'

  return {
    body: isDark ? theme.colors.dark[8] : COLORS.white,
    hero: isDark ? theme.colors.dark[7] : COLORS.background,
    sectionAlt: isDark ? theme.colors.dark[7] : COLORS.background,
    surface: isDark ? theme.colors.dark[6] : COLORS.white,
    surfaceMuted: isDark ? theme.colors.dark[7] : 'var(--sat-surface-alt)',
    text: isDark ? theme.white : COLORS.sunkenShip,
    mutedText: isDark ? theme.colors.gray[3] : COLORS.eveningSea,
    dimmedText: isDark ? theme.colors.dark[2] : 'var(--sat-text-muted)',
    border: isDark ? theme.colors.dark[4] : 'var(--sat-border-soft)',
    previewBorder: isDark ? theme.colors.dark[4] : 'var(--sat-preview-border)',
    header: isDark ? theme.colors.dark[7] : COLORS.white,
    footer: isDark ? theme.colors.dark[9] : COLORS.sunkenShip,
    footerBorder: isDark ? theme.colors.dark[4] : COLORS.eveningSea,
    footerText: isDark ? theme.colors.gray[4] : COLORS.terraCotta,
    sidebarText: isDark ? theme.colors.gray[5] : 'var(--sat-text-muted)',
    activeSidebarBg: 'var(--sat-red-soft-alt)',
  }
}

/* ------------------------------------------------------------------ */
/*  DASHBOARD PREVIEW (fake data card for hero)                       */
/* ------------------------------------------------------------------ */
function DashboardPreview() {
  const colors = useLandingColors()
  const sidebarItems = [
    'Home',
    'Drills',
    'Question Bank',
    'Mock Tests',
    'Study Plan',
    'Vocabulary',
    'Courses',
    'Leaderboard',
    'AI Tutor',
  ]

  return (
    <Paper
      radius="lg"
      shadow="xl"
      p={0}
      style={{
        overflow: 'hidden',
        border: `1px solid ${colors.previewBorder}`,
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <Flex>
        

        {/* Main content area */}
        <Box style={{ flex: 1, background: colors.surfaceMuted, padding: '20px 24px' }}>
          <Flex justify="space-between" align="center" mb={16}>
            <Box>
              <Text fz="xs" c={colors.mutedText} fw={500}>
                Welcome back, Jared
              </Text>
              <Text fz={10} c={colors.dimmedText}>
                Your SAT journey is on track
              </Text>
            </Box>
            <Badge color={COLORS.grapefruit} variant="light" size="sm">
              Advanced
            </Badge>
          </Flex>

          <SimpleGrid cols={{ base: 2, xs: 3 }} spacing="sm">
            {/* Target Score */}
            <Paper radius="md" p="sm" shadow="xs" style={{ background: colors.surface }}>
              <Group gap={6} mb={6}>
                <Target size={14} color={COLORS.grapefruit} />
                <Text fz={10} c={colors.dimmedText} fw={500}>
                  Target Score
                </Text>
              </Group>
              <Text fz={28} fw={800} c={colors.text} lh={1}>
                1580
              </Text>
              <Text fz={10} c={colors.mutedText}>
                Current: 1500
              </Text>
            </Paper>

            {/* Day Streak */}
            <Paper radius="md" p="sm" shadow="xs" style={{ background: colors.surface }}>
              <Group gap={6} mb={6}>
                <Zap size={14} color={COLORS.lemon} />
                <Text fz={10} c={colors.dimmedText} fw={500}>
                  Day Streak
                </Text>
              </Group>
              <Text fz={28} fw={800} c={colors.text} lh={1}>
                15
              </Text>
              <Text fz={10} c={colors.mutedText}>
                Personal best!
              </Text>
            </Paper>

            {/* XP Points */}
            <Paper radius="md" p="sm" shadow="xs" style={{ background: colors.surface }}>
              <Group gap={6} mb={6}>
                <Trophy size={14} color={COLORS.lemon} />
                <Text fz={10} c={colors.dimmedText} fw={500}>
                  XP Points
                </Text>
              </Group>
              <Text fz={28} fw={800} c={colors.text} lh={1}>
                1,600
              </Text>
              <Text fz={10} c={colors.mutedText}>
                Level 8
              </Text>
            </Paper>
          </SimpleGrid>

          {/* Progress bars */}
          <Paper radius="md" p="sm" mt="sm" shadow="xs" style={{ background: colors.surface }}>
            <Text fz={10} fw={600} c={colors.text} mb={8}>
              Today's Progress
            </Text>
            <Stack gap={6}>
              <Box>
                <Group justify="space-between" mb={2}>
                  <Text fz={10} c={colors.mutedText}>
                    Reading & Writing
                  </Text>
                  <Text fz={10} c={colors.dimmedText}>
                    58%
                  </Text>
                </Group>
                <Progress value={58} color={COLORS.eveningSea} size="xs" radius="xl" />
              </Box>
              <Box>
                <Group justify="space-between" mb={2}>
                  <Text fz={10} c={colors.mutedText}>
                    Math
                  </Text>
                  <Text fz={10} c={colors.dimmedText}>
                    84%
                  </Text>
                </Group>
                <Progress value={94} color={COLORS.grapefruit} size="xs" radius="xl" />
              </Box>
              <Box>
                <Group justify="space-between" mb={2}>
                  <Text fz={10} c={colors.mutedText}>
                    Vocabulary
                  </Text>
                  <Text fz={10} c={colors.dimmedText}>
                    80%
                  </Text>
                </Group>
                <Progress value={80} color={COLORS.lemon} size="xs" radius="xl" />
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Flex>
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  SECTION WRAPPER                                                   */
/* ------------------------------------------------------------------ */
function Section({
  id,
  bg,
  children,
  py = 80,
}: {
  id?: string
  bg?: string
  children: React.ReactNode
  py?: number
}) {
  const colors = useLandingColors()
  const background = bg === COLORS.background ? colors.sectionAlt : bg || colors.surface

  return (
    <Box id={id} style={{ background }} py={py}>
      <Container size="lg">{children}</Container>
    </Box>
  )
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  const colors = useLandingColors()

  return (
    <Stack align="center" gap={8} mb={48}>
      <Title order={2} ta="center" fz={{ base: 30, sm: 38 }} c={colors.text}>
        {children}
      </Title>
      {sub && (
        <Text ta="center" c={colors.mutedText} maw={560} fz="lg">
          {sub}
        </Text>
      )}
    </Stack>
  )
}

/* ------------------------------------------------------------------ */
/*  PRICING CARD                                                      */
/* ------------------------------------------------------------------ */
function planPeriodLabel(planCode: string): string {
  const code = planCode.toUpperCase()
  if (code.includes('MON')) return '/ month'
  if (code.includes('ANN')) return '/ year'
  return 'Lifetime Access'
}

function PricingCard({
  plan,
  highlight = false,
  colors,
  onStart,
}: {
  plan: SatPlan | null
  highlight?: boolean
  colors: ReturnType<typeof useLandingColors>
  onStart: () => void
}) {
  const price = plan ? `$${(plan.price_in_cent / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '$1,900'
  const period = plan ? planPeriodLabel(plan.plan_code) : 'Lifetime Access'
  const name = plan?.name ?? 'Lifetime'
  const description = plan?.description ?? null

  const FEATURES = [
    'Unlimited SAT question practice',
    'AI-powered question generation',
    'Smart vocabulary flashcards with spaced repetition',
    'Real-time progress tracking & dashboards',
    ...(!plan || plan.platform_type !== 'self_pace'
      ? ['Tutor roster management & assignment system']
      : ['Self-paced study plan with daily goals']),
  ]

  return (
    <Card
      radius="lg"
      padding="xl"
      shadow="lg"
      maw={460}
      w="100%"
      style={{
        borderTop: `4px solid ${COLORS.grapefruit}`,
        background: colors.surface,
      }}
    >
      <Stack align="center" gap="md">
     {/*    {highlight && (
          <Badge size="lg" color={COLORS.grapefruit} variant="light" radius="xl">
            Best Value
          </Badge>
        )} */}
        <Text fz="lg" fw={600} c={colors.text}>{name}</Text>
        <Text fz={52} fw={800} c={COLORS.grapefruit} lh={1}>
          {price}
        </Text>
        <Text fz="sm" fw={500} c={colors.mutedText}>{period}</Text>
        {description && (
          <Text fz="sm" c={colors.dimmedText} ta="center">{description}</Text>
        )}
        <Divider w="100%" color={colors.border} />
        <List
          spacing="sm"
          size="sm"
          c={colors.mutedText}
          icon={
            <ThemeIcon size={22} radius="xl" color={COLORS.eveningSea}>
              <Check size={13} />
            </ThemeIcon>
          }
        >
          {FEATURES.map((f) => <List.Item key={f}>{f}</List.Item>)}
        </List>
        <Button
          fullWidth
          size="lg"
          radius="xl"
          color={COLORS.grapefruit}
          mt="md"
          rightSection={<ArrowRight size={18} />}
          onClick={onStart}
          styles={{ root: { fontWeight: 700 } }}
        >
          Start Your Journey
        </Button>
      </Stack>
    </Card>
  )
}

/* ------------------------------------------------------------------ */
/*  MAIN LANDING PAGE                                                 */
/* ------------------------------------------------------------------ */
export function Home() {
  const navigate = useNavigate()
  const location = useLocation()
  const [platformType, setPlatformType] = useState<string>('tutoring')
  const [plans, setPlans] = useState<SatPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  const colors = useLandingColors()
  const isSelfPace = platformType === 'self_pace'

  useEffect(() => {
    Promise.all([paymentApi.getPlatformType(), paymentApi.getPublicPlans()])
      .then(([ptRes, plansRes]) => {
        setPlatformType(ptRes.data.platform_type)
        setPlans(plansRes.data)
      })
      .catch(() => {})
      .finally(() => setPlansLoading(false))
  }, [])

  useEffect(() => {
    if (!location.hash) {
      return
    }

    const sectionId = location.hash.slice(1)
    const scrollToSection = () => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
    }

    requestAnimationFrame(scrollToSection)
  }, [location.hash])

  return (
    <Box style={{ background: colors.body }}>
      {/* ---- HEADER ---- */}
      <PublicLandingHeader />

      {/* ---- HERO ---- */}
      <Box
        id="mission"
        style={{
          background: colors.hero,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container size="lg" py={60}>
          <Stack align="center" gap="xl">
            <Badge
              size="lg"
              radius="xl"
              variant="light"
              color={COLORS.grapefruit}
              style={{ textTransform: 'none' }}
            >
              AI-Assisted SAT Preparation
            </Badge>

            <Title
              order={1}
              ta="center"
              fz={{ base: 36, sm: 48, md: 56 }}
              lh={1.15}
              c={colors.text}
              maw={720}
              fw={800}
            >
              {isSelfPace ? 'Self Paced Adaptive SAT Practice' : 'One on One SAT Tutor'}
            </Title>
            <Text
              ta="center"
              size="40px"
              fw={800}
              c={colors.text}
            >
              {isSelfPace ? 'Empowered by AI to Ace the SAT Exam' : 'Guide student acing the SAT Exam'}
            </Text>
            <Text
              ta="center"
              size="40px"
              fw={800}
              c={colors.text}
            >
              in less than 30 days
            </Text>

            <Text
              ta="center"
              fz={{ base: 'md', sm: 'lg' }}
              c={colors.mutedText}
              maw={620}
              lh={1.6}
            >
              {!isSelfPace && (
                <>Tutors make plans, assign tasks, guide and monitor learning;<br /></>
              )}
              Intensive and truly adaptive SAT full-length mock exams;<br />
              Crush course and steps to master 30 SAT skill points;<br />
              AI Identify weaknesses, provide personalized practice;<br />
              Smart vocabulary flashcards;<br />
              Achieve your dream score with our intelligent preparation platform;
            </Text>

            <Group gap="md" mt={8}>
              <Button
                size="lg"
                radius="xl"
                color={COLORS.grapefruit}
                rightSection={<ArrowRight size={18} />}
                onClick={() => navigate('/register')}
                styles={{
                  root: {
                    paddingLeft: 28,
                    paddingRight: 24,
                    fontWeight: 700,
                    fontSize: 16,
                  },
                }}
              >
                Start Your Journey 
              </Button>
              <Button
                size="lg"
                radius="xl"
                variant="outline"
                color={COLORS.eveningSea}
                onClick={() =>
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                See How It Works
              </Button>
            </Group>

            <Box mt={32} w="100%">
              <DashboardPreview />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ---- FEATURES ---- */}
      <Section id="features">
        <SectionTitle>Everything You Need to Ace the SAT</SectionTitle>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          {/* AI-Powered Questions */}
          <Card
            radius="lg"
            padding="xl"
            withBorder
            style={{ borderColor: colors.border, background: colors.surface }}
          >
            <ThemeIcon size={48} radius="md" color={COLORS.grapefruit} mb="md">
              <Brain size={24} />
            </ThemeIcon>
            <Title order={4} c={colors.text} mb={8}>
              AI-Powered Questions
            </Title>
            <Text c={colors.mutedText} fz="sm" lh={1.7}>
              Smart question generation that adapts to your skill level. Practice with
              thousands of SAT-style questions across all domains.
            </Text>
          </Card>

          {/* Smart Vocabulary */}
          <Card
            radius="lg"
            padding="xl"
            withBorder
            style={{ borderColor: colors.border, background: colors.surface }}
          >
            <ThemeIcon size={48} radius="md" color={COLORS.eveningSea} mb="md">
              <BookA size={24} />
            </ThemeIcon>
            <Title order={4} c={colors.text} mb={8}>
              Smart Vocabulary
            </Title>
            <Text c={colors.mutedText} fz="sm" lh={1.7}>
              Spaced repetition flashcards with swipe gestures. Learn 20 new SAT words
              daily and master them through intelligent review.
            </Text>
          </Card>

          {/* Tutor-Guided Learning */}
          <Card
            radius="lg"
            padding="xl"
            withBorder
            style={{ borderColor: colors.border, background: colors.surface }}
          >
            <ThemeIcon size={48} radius="md" color={COLORS.lemon} variant="filled" mb="md">
              <GraduationCap size={24} color={COLORS.sunkenShip} />
            </ThemeIcon>
            <Title order={4} c={colors.text} mb={8}>
              AI assisted Learning
            </Title>
            <Text c={colors.mutedText} fz="sm" lh={1.7}>
                Expert tutors create personalized assignments, grade your work, and generate
                targeted review materials.
            </Text>
          </Card>

          {/* Progress Tracking */}
          <Card
            radius="lg"
            padding="xl"
            withBorder
            style={{ borderColor: colors.border, background: colors.surface }}
          >
            <ThemeIcon size={48} radius="md" color={COLORS.eveningSea} mb="md">
              <BarChart3 size={24} />
            </ThemeIcon>
            <Title order={4} c={colors.text} mb={8}>
              Progress Tracking
            </Title>
            <Text c={colors.mutedText} fz="sm" lh={1.7}>
              Real-time dashboards with mastery radar charts, XP points, day streaks, and
              detailed performance analytics.
            </Text>
          </Card>
        </SimpleGrid>
      </Section>

      {/* ---- HOW IT WORKS ---- */}
      <Section id="how-it-works" bg={COLORS.background} >
        <SectionTitle>Get Started in 3 Simple Steps</SectionTitle>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
          {[
            {
              step: '1',
              title: 'Register',
              desc: 'Register as a student,set up your profile and choose your plan.',
            },
            {
              step: '2',
              title: 'Get Assigned',
              desc: 'Students receive personalized daily learning plans.',
            },
            {
              step: '3',
              title: 'Practice & Improve',
              desc: 'Complete assignments,  track your progress to SAT mastery.',
            },
          ].map((item, i) => (
            <Stack key={item.step} align="center" gap="md">
              <Center
                w={56}
                h={56}
                style={{
                  borderRadius: '50%',
                  background: COLORS.eveningSea,
                  fontWeight: 800,
                  fontSize: 22,
                  color: COLORS.sunkenShip,
                  boxShadow: `0 4px 12px var(--sat-yellow-glow)`,
                }}
              >
                {item.step}
              </Center>
              {/* Dashed connector hidden on mobile */}
              {i < 2 && (
                <Box
                  visibleFrom="sm"
                  style={{
                    position: 'absolute',
                    display: 'none', // handled via relative layout below
                  }}
                />
              )}
              <Title order={4} c={colors.text} ta="center">
                {item.title}
              </Title>
              <Text fz="sm" c={colors.mutedText} ta="center" maw={280} lh={1.7}>
                {item.desc}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>

        {/* Dashed connector line (desktop only) */}
        <Box
          visibleFrom="sm"
          mt={-200}
          mb={160}
          mx="auto"
          style={{
            maxWidth: 520,
            height: 2,
            borderTop: `2px dashed ${COLORS.terraCotta}`,
            position: 'relative',
            top: -72,
            pointerEvents: 'none',
          }}
        />
      </Section>

      {/* ---- SAT COVERAGE ---- */}
      <Section id="coverage">
        <SectionTitle sub="Master every domain tested on the Digital SAT">
          Complete SAT Coverage
        </SectionTitle>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={40}>
          {/* Reading & Writing */}
          <Box>
            <Group gap="sm" mb="lg">
              <ThemeIcon size={36} radius="md" color={COLORS.eveningSea}>
                <BookOpen size={18} />
              </ThemeIcon>
              <Title order={4} c={colors.text}>
                Reading & Writing
              </Title>
            </Group>
            <Stack gap="md">
              {[
                { name: 'Craft and Structure', pct: 28 },
                { name: 'Information and Ideas', pct: 26 },
                { name: 'Standard English Conventions', pct: 26 },
                { name: 'Expression of Ideas', pct: 20 },
              ].map((d) => (
                <Box key={d.name}>
                  <Group justify="space-between" mb={4}>
                    <Text fz="sm" fw={500} c={colors.text}>
                      {d.name}
                    </Text>
                    <Text fz="sm" c={colors.dimmedText}>
                      {d.pct}%
                    </Text>
                  </Group>
                  <Progress value={d.pct} color={COLORS.eveningSea} size="sm" radius="xl" />
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Math */}
          <Box>
            <Group gap="sm" mb="lg">
              <ThemeIcon size={36} radius="md" color={COLORS.grapefruit}>
                <BarChart3 size={18} />
              </ThemeIcon>
              <Title order={4} c={colors.text}>
                Math
              </Title>
            </Group>
            <Stack gap="md">
              {[
                { name: 'Algebra', pct: 35 },
                { name: 'Advanced Math', pct: 35 },
                { name: 'Problem-Solving & Data Analysis', pct: 15 },
                { name: 'Geometry & Trigonometry', pct: 15 },
              ].map((d) => (
                <Box key={d.name}>
                  <Group justify="space-between" mb={4}>
                    <Text fz="sm" fw={500} c={colors.text}>
                      {d.name}
                    </Text>
                    <Text fz="sm" c={colors.dimmedText}>
                      {d.pct}%
                    </Text>
                  </Group>
                  <Progress value={d.pct} color={COLORS.grapefruit} size="sm" radius="xl" />
                </Box>
              ))}
            </Stack>
          </Box>
        </SimpleGrid>
      </Section>

      {/* ---- PRICING ---- */}
      <Section id="pricing" bg={COLORS.background}>
        <SectionTitle>Simple Pricing</SectionTitle>

        {plansLoading ? (
          <Center py="xl"><Loader color={COLORS.grapefruit} /></Center>
        ) : plans.length === 0 ? (
          /* Fallback card when no plans are seeded yet */
          <Center>
            <PricingCard
              plan={null}
              colors={colors}
              onStart={() => navigate('/register')}
            />
          </Center>
        ) : (
          <SimpleGrid
            cols={{ base: 1, sm: plans.length === 1 ? 1 : 2, md: Math.min(plans.length, 3) }}
            spacing="xl"
            style={{ justifyItems: 'center' }}
          >
            {plans.map((plan, i) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                highlight={i === 0}
                colors={colors}
                onStart={() => navigate('/register')}
              />
            ))}
          </SimpleGrid>
        )}
      </Section>
    </Box>
  )
}
